<script setup>
import { ref, computed } from 'vue'
import { Head, useForm } from '@inertiajs/vue3'
import {
    PhCamera, PhUser, PhEnvelope, PhPhone, PhMapPin,
    PhCalendar, PhFloppyDisk, PhX, PhPencilSimple, PhLockKey,
} from "@phosphor-icons/vue"
import Layout   from '@pages/Shared/layouts/AdminLayout.vue'
import Spinner  from '@pages/Shared/ui/Spinner.vue'
import Input    from '@pages/Shared/form/Input.vue'
import Select   from '@pages/Shared/form/Select.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import { flashToast } from "@/toast.js"
import { formatDate   } from '@/helpers.js'
import userMale   from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'

/* -------------------- PROPS -------------------- */
const props = defineProps({ user: Object })

/* -------------------- STATE -------------------- */
const isEditing = ref(false)
const fileInput = ref(null)

/* -------------------- FORMS -------------------- */
const form = useForm({
    avatar:   props.user?.avatar  || null,
    preview:  null,
    name:     props.user?.name    || '',
    email:    props.user?.email   || '',
    phone:    props.user?.phone   || '',
    gender:   props.user?.gender  || 'Male',
    dob:      props.user?.dob     || '',
    address:  props.user?.address || '',
    role:     props.user?.role?.name || 'User',
    status:   props.user?.status  || 'Active',
})

const passwordForm = useForm({
    current_password:      '',
    password:              '',
    password_confirmation: '',
})

/* -------------------- COMPUTED -------------------- */
const avatarSrc = computed(() => {
    if (form.preview) return form.preview
    if (form.avatar)  return `/storage/${form.avatar}`
    return form.gender === 'Male' ? userMale : userFemale
})

/* -------------------- HANDLERS -------------------- */
const handleFileSelected = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (form.preview) URL.revokeObjectURL(form.preview)
    form.avatar  = file
    form.preview = URL.createObjectURL(file)
}

const handleSubmit = () => {
    form.put('/admin/profile', {
        onSuccess: (page) => { isEditing.value = false; flashToast(page.props.flash) },
        onError:   ()     => flashToast({ type: 'error', message: 'Check form for errors' }),
    })
}

const handleCancel = () => {
    if (form.preview) URL.revokeObjectURL(form.preview)
    isEditing.value = false
    form.reset()
}

const updatePassword = () => {
    passwordForm.patch('/admin/profile', {
        onSuccess: (page) => { passwordForm.reset(); flashToast(page.props.flash) },
        onError:   ()     => flashToast({ type: 'error', message: 'Check form for errors' }),
    })
}

const genderOptions = [
    { value: 'Male',   label: 'Male'   },
    { value: 'Female', label: 'Female' },
]

// Read-only field display items
const infoFields = computed(() => [
    { icon: PhEnvelope, label: 'Email Address',  value: form.email              },
    { icon: PhPhone,    label: 'Phone Number',   value: form.phone || 'N/A'     },
    { icon: PhCalendar, label: 'Date of Birth',  value: form.dob ? formatDate(form.dob) : 'N/A' },
    { icon: null,       label: 'Gender',         value: form.gender             },
    { icon: null,       label: 'Role',           value: form.role               },
])
</script>

<template>
    <Layout>
        <Head title="Profile Settings" />

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-gray-800">Profile Settings</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage your personal information and account settings</p>
            </div>
        </div>

        <!-- Profile Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">

            <!-- Cover -->
            <div class="h-32 bg-gradient-to-br from-primary via-primary to-primary-light relative overflow-hidden"><div class="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/5"></div><div class="absolute right-24 -bottom-6 w-24 h-24 rounded-full bg-white/5"></div></div>

            <!-- Avatar + Name row -->
            <div class="px-6 sm:px-8 pb-6">
                <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between -mt-12">

                    <!-- Avatar -->
                    <div class="flex flex-col sm:flex-row items-center sm:items-end gap-4">
                        <div class="relative group">
                            <div class="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-100">
                                <img :src="avatarSrc" alt="Avatar" class="w-full h-full object-cover" />
                            </div>
                            <button
                                v-if="isEditing"
                                type="button"
                                @click="fileInput?.click()"
                                class="absolute inset-0 flex items-center justify-center bg-black/50
                                       rounded-full opacity-0 group-hover:opacity-100 transition-all"
                            >
                                <PhCamera :size="20" class="text-white" weight="bold" />
                            </button>
                            <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handleFileSelected" />
                        </div>

                        <div class="text-center sm:text-left mb-2 sm:mb-0">
                            <h2 class="text-lg font-bold text-white drop-shadow">{{ form.name }}</h2>
                            <p class="text-sm text-primary-light mt-0.5 capitalize">{{ form.role }}</p>
                            <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 mt-1.5 rounded-full text-xs font-medium border"
                                :class="form.status === 'Active'
                                    ? 'bg-green-50 text-green-700 border-green-100'
                                    : 'bg-orange-50 text-orange-600 border-orange-100'">
                                <span class="w-1.5 h-1.5 rounded-full"
                                    :class="form.status === 'Active' ? 'bg-green-500' : 'bg-orange-400'"></span>
                                {{ form.status }}
                            </span>
                        </div>
                    </div>

                    <!-- Action buttons -->
                    <div class="flex justify-center sm:justify-end gap-2 mt-4 sm:mt-0">
                        <button v-if="!isEditing" type="button" @click="isEditing = true"
                            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold
                                   text-white bg-primary hover:bg-primary-light rounded-lg transition">
                            <PhPencilSimple :size="15" weight="bold" /> Edit Profile
                        </button>
                        <template v-else>
                            <button type="button" @click="handleCancel"
                                class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
                                       text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-200 transition">
                                <PhX :size="15" weight="bold" /> Cancel
                            </button>
                            <button type="button" @click="handleSubmit" :disabled="form.processing"
                                class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold
                                       text-white bg-edit hover:bg-edit-light rounded-lg transition
                                       disabled:opacity-50 disabled:cursor-not-allowed">
                                <Spinner v-if="form.processing" :size="14" />
                                <PhFloppyDisk v-else :size="15" weight="bold" />
                                Save Changes
                            </button>
                        </template>
                    </div>
                </div>
            </div>

            <div class="border-t border-gray-100"></div>

            <!-- Details -->
            <div class="p-6 sm:p-8">

                <!-- Personal Information -->
                <div class="mb-8">
                    <div class="flex items-center gap-2 mb-5">
                        <PhUser :size="16" class="text-primary" weight="bold" />
                        <h3 class="text-sm font-semibold text-gray-800">Personal Information</h3>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">

                        <!-- Full Name -->
                        <Input v-if="isEditing" v-model="form.name" label="Full Name"
                            placeholder="Enter your full name" :error="form.errors.name" />
                        <div v-else>
                            <label class="block text-sm font-medium text-gray-600 mb-1.5">Full Name</label>
                            <div class="px-3.5 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-200">
                                {{ form.name }}
                            </div>
                        </div>

                        <!-- Email -->
                        <Input v-if="isEditing" v-model="form.email" type="email" label="Email Address"
                            placeholder="Enter your email" :error="form.errors.email" />
                        <div v-else>
                            <label class="block text-sm font-medium text-gray-600 mb-1.5">Email Address</label>
                            <div class="flex items-center gap-2 px-3.5 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-200">
                                <PhEnvelope :size="14" class="text-gray-400 shrink-0" />
                                {{ form.email }}
                            </div>
                        </div>

                        <!-- Phone -->
                        <Input v-if="isEditing" v-model="form.phone" type="tel" label="Phone Number"
                            placeholder="Enter your phone number" :error="form.errors.phone" />
                        <div v-else>
                            <label class="block text-sm font-medium text-gray-600 mb-1.5">Phone Number</label>
                            <div class="flex items-center gap-2 px-3.5 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-200">
                                <PhPhone :size="14" class="text-gray-400 shrink-0" />
                                {{ form.phone || 'N/A' }}
                            </div>
                        </div>

                        <!-- Date of Birth -->
                        <Input v-if="isEditing" v-model="form.dob" type="date" label="Date of Birth"
                            :error="form.errors.dob" />
                        <div v-else>
                            <label class="block text-sm font-medium text-gray-600 mb-1.5">Date of Birth</label>
                            <div class="flex items-center gap-2 px-3.5 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-200">
                                <PhCalendar :size="14" class="text-gray-400 shrink-0" />
                                {{ form.dob ? formatDate(form.dob) : 'N/A' }}
                            </div>
                        </div>

                        <!-- Gender -->
                        <Select v-if="isEditing" v-model="form.gender" :options="genderOptions"
                            label="Gender" :error="form.errors.gender" />
                        <div v-else>
                            <label class="block text-sm font-medium text-gray-600 mb-1.5">Gender</label>
                            <div class="px-3.5 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-200">
                                {{ form.gender }}
                            </div>
                        </div>

                        <!-- Role (always read-only) -->
                        <div>
                            <label class="block text-sm font-medium text-gray-600 mb-1.5">Role</label>
                            <div class="px-3.5 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-200 capitalize">
                                {{ form.role }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Address -->
                <div>
                    <div class="flex items-center gap-2 mb-5">
                        <PhMapPin :size="16" class="text-primary" weight="bold" />
                        <h3 class="text-sm font-semibold text-gray-800">Address</h3>
                    </div>
                    <Textarea v-if="isEditing" v-model="form.address" :rows="3" label="Address"
                        placeholder="Enter your address" :error="form.errors.address" />
                    <div v-else class="px-3.5 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-200 min-h-[60px]">
                        {{ form.address || 'N/A' }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Security Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
            <div class="flex items-center gap-2 mb-6">
                <PhLockKey :size="16" class="text-primary" weight="bold" />
                <h3 class="text-sm font-semibold text-gray-800">Security Settings</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div class="md:col-span-2">
                    <Input v-model="passwordForm.current_password" type="password"
                        label="Current Password" placeholder="Enter current password"
                        :error="passwordForm.errors.current_password" />
                </div>
                <Input v-model="passwordForm.password" type="password"
                    label="New Password" placeholder="Enter new password"
                    :error="passwordForm.errors.password" />
                <Input v-model="passwordForm.password_confirmation" type="password"
                    label="Confirm Password" placeholder="Confirm new password"
                    :error="passwordForm.errors.password_confirmation" />
            </div>

            <div class="mt-6 flex justify-end">
                <button type="button" @click="updatePassword" :disabled="passwordForm.processing"
                    class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                           text-white bg-primary hover:bg-primary-light rounded-lg transition
                           disabled:opacity-50 disabled:cursor-not-allowed">
                    <Spinner v-if="passwordForm.processing" :size="14" />
                    <PhLockKey v-else :size="15" weight="bold" />
                    Update Password
                </button>
            </div>
        </div>

    </Layout>
</template>